import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Settings, 
  Shield, 
  BarChart3, 
  Heart, 
  Cog,
  Info,
  CheckCircle,
  XCircle,
  Megaphone
} from "lucide-react";
import { useCookiePreferences, type CookiePreferences, applyConsentMode } from "@/hooks/useCookiePreferences";

interface GDPRCookieBannerProps {
  onAcceptAll?: () => void;
  onRejectAll?: () => void;
  onSavePreferences?: (preferences: CookiePreferences) => void;
}

export function GDPRCookieBanner({ 
  onAcceptAll, 
  onRejectAll, 
  onSavePreferences 
}: GDPRCookieBannerProps) {
  const { t } = useTranslation();
  const { 
    preferences, 
    updatePreferences, 
    acceptAll, 
    rejectAll, 
    hasConsent,
    isLoaded 
  } = useCookiePreferences();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showBanner) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showBanner]);

  useEffect(() => {
    if (isLoaded && !hasConsent()) {
      setShowBanner(true);
    }
  }, [isLoaded, hasConsent]);

  const handleAcceptAll = () => {
    acceptAll();
    setShowBanner(false);
    onAcceptAll?.();
  };

  const handleRejectAll = () => {
    rejectAll();
    setShowBanner(false);
    onRejectAll?.();
  };

  const handleSavePreferences = () => {
    updatePreferences(preferences);
    applyConsentMode(preferences);
    setShowBanner(false);
    setShowSettings(false);
    onSavePreferences?.(preferences);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences, value: boolean) => {
    if (category === 'essential') return; // Essential cookies cannot be disabled
    updatePreferences({
      ...preferences,
      [category]: value
    });
  };

  const getCookieIcon = (category: keyof CookiePreferences) => {
    switch (category) {
      case 'essential': return <Shield className="w-4 h-4" />;
      case 'analytics': return <BarChart3 className="w-4 h-4" />;
      case 'preferences': return <Heart className="w-4 h-4" />;
      case 'functionality': return <Cog className="w-4 h-4" />;
      case 'advertising': return <Megaphone className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getCookieStatus = (category: keyof CookiePreferences) => {
    if (preferences[category]) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl h-[90vh] lg:h-[70vh]">
        <Card className="shadow-2xl border-0 h-full flex flex-col">
          {/* Header with tabs */}
          <div className="border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center p-4 pb-0">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-primary/10 rounded">
                  <Shield className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Statemia
                </span>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex px-4">
              <button
                onClick={() => setShowSettings(false)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  !showSettings
                    ? 'border-primary text-primary'
                    : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {t('gdpr.consent')}
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  showSettings
                    ? 'border-primary text-primary'
                    : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {t('gdpr.details')}
              </button>
            </div>
          </div>

          <CardContent className="p-6 flex-1 flex flex-col overflow-hidden">
            {!showSettings ? (
              // Consent Tab
              <div className="flex-1 flex flex-col min-h-0">
                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto">
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-3">
                        {t('gdpr.consentTitle')}
                      </h2>
                      <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                        {t('gdpr.consentMessage')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Fixed Action Buttons */}
                <div className="flex-shrink-0 pt-6">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      onClick={handleRejectAll}
                      className="flex-1 bg-white hover:bg-slate-50 border-slate-300 text-slate-800 min-h-[44px] font-medium"
                    >
                      {t('gdpr.rejectAll')}
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => setShowSettings(true)}
                      className="flex-1 bg-white hover:bg-slate-50 border-slate-300 text-slate-700 min-h-[44px]"
                    >
                      {t('gdpr.customize')} →
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={handleAcceptAll}
                      className="flex-1 bg-white hover:bg-slate-50 border-slate-300 text-slate-800 min-h-[44px] font-medium"
                    >
                      {t('gdpr.acceptAll')}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // Details Tab
              <div className="flex-1 flex flex-col min-h-0">
                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto">
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-3">
                        {t('gdpr.categories.title')}
                      </h2>
                      <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                        {t('gdpr.detailsMessage')}
                      </p>
                    </div>

                    {/* Cookie Categories */}
                    <div className="space-y-3">
                      {Object.entries(preferences).map(([category, enabled]) => (
                        <div key={category} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {getCookieIcon(category as keyof CookiePreferences)}
                              <div>
                                <h4 className="font-medium text-slate-900 dark:text-white">
                                  {t(`gdpr.categories.${category}.title`)}
                                </h4>
                                <p className="text-sm text-slate-600 dark:text-slate-300">
                                  {t(`gdpr.categories.${category}.description`)}
                                </p>
                              </div>
                            </div>
                            <Switch
                              checked={enabled}
                              onCheckedChange={(value) => handlePreferenceChange(category as keyof CookiePreferences, value)}
                              disabled={category === 'essential'}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Fixed Action Buttons */}
                <div className="flex-shrink-0 pt-6">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowSettings(false)}
                      className="flex-1 min-h-[44px]"
                    >
                      {t('gdpr.back')}
                    </Button>
                    <Button
                      onClick={handleSavePreferences}
                      className="flex-1 bg-primary hover:bg-primary/90 text-white min-h-[44px]"
                    >
                      {t('gdpr.savePreferences')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Footer Links */}
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
              <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-400">
                <div className="flex space-x-4">
                  <button
                    onClick={() => window.open('/politica-de-cookies', '_blank')}
                    className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                  >
                    {t('gdpr.learnMore')}
                  </button>
                  <button
                    onClick={() => window.open('/politica-de-privacidad', '_blank')}
                    className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                  >
                    {t('gdpr.privacyPolicy')}
                  </button>
                </div>
                <span>{t('gdpr.lastUpdated')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
